import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";
import { ApiContext } from "../../../../App";

export const PRICE_ENGINE_TO_TEXT = {
    fixed: "Fixed",
    day_night: "Day-Night",
    octopus: "Octopus",
    epias: "EPIAS electricity",
    epias_gas: "EPIAS gas",
};

// The fields each price engine needs. The API rejects the others, so they're
// sent as null. Octopus and EPIAS prices come from the price getters.
const PRICE_ENGINE_FIELDS = {
    fixed: ["value"],
    day_night: [
        "value",
        "night_value",
        "day_tariff_start_time",
        "day_tariff_end_time",
    ],
};

const PRICE_FIELDS = [
    "value",
    "night_value",
    "day_tariff_start_time",
    "day_tariff_end_time",
];

function TariffEditForm(props) {
    const { value, onChange, errorMessages } = props;

    const { t } = useTranslation();

    const shown = PRICE_ENGINE_FIELDS[value?.price_engine] ?? [];

    const textField = (id, label, extraProps = {}) => (
        <TextField
            variant="standard"
            id={id}
            label={label}
            value={value[id] ?? ""}
            onChange={(event) => onChange(id, event.target.value)}
            error={errorMessages.hasOwnProperty(id)}
            helperText={errorMessages[id] ? errorMessages[id] : ""}
            {...extraProps}
        />
    );

    return value ? (
        <Stack spacing={2}>
            {textField("name", t("Name"))}
            {textField("price_engine", t("Price engine"), {
                select: true,
                children: Object.entries(PRICE_ENGINE_TO_TEXT).map(
                    ([engine, text]) => (
                        <MenuItem key={engine} value={engine}>
                            {t(text)}
                        </MenuItem>
                    ),
                ),
            })}
            {shown.includes("value") && textField("value", t("Value"))}
            {shown.includes("night_value") &&
                textField("night_value", t("Night Value"))}
            {shown.includes("day_tariff_start_time") &&
                textField("day_tariff_start_time", t("Day tariff start time"), {
                    placeholder: "HH:MM",
                    InputLabelProps: { shrink: true },
                    value: (value.day_tariff_start_time ?? "").slice(0, 5),
                })}
            {shown.includes("day_tariff_end_time") &&
                textField("day_tariff_end_time", t("Day tariff end time"), {
                    placeholder: "HH:MM",
                    InputLabelProps: { shrink: true },
                    value: (value.day_tariff_end_time ?? "").slice(0, 5),
                })}
            {textField("carbon_intensity_g_kwh", t("Carbon intensity (g/kWh)"))}
        </Stack>
    ) : null;
}

export default function MeteringTariffEditPopUp(props) {
    const { open, onCloseWithoutSaving, onCloseWithUpdate } = props;

    const { t } = useTranslation();

    const [data, setData] = React.useState(props.data);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [errorMessages, setErrorMessages] = React.useState({});

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const { meteringTariffApi } = React.useContext(ApiContext);

    React.useEffect(() => {
        setData(props.data);
    }, [props.data]);

    const handleFormChange = (id, value) => {
        setData((old) => ({ ...old, [id]: value }));
        setErrorMessages((old) =>
            Object.fromEntries(
                Object.entries(old).filter(([key]) => key !== id),
            ),
        );
    };

    const handleCloseWithoutSaving = () => {
        setData(props.data);
        onCloseWithoutSaving();
        setErrorMessages({});
    };

    const handleSave = () => {
        setErrorMessages({});

        // Blank fields are sent as null, which the API's decimal and time
        // fields accept, rather than "".
        const shown = PRICE_ENGINE_FIELDS[data.price_engine] ?? [];
        const payload = {
            facility: data.facility,
            name: data.name ?? "",
            price_engine: data.price_engine ?? "",
            carbon_intensity_g_kwh:
                data.carbon_intensity_g_kwh === ""
                    ? null
                    : (data.carbon_intensity_g_kwh ?? null),
            ...Object.fromEntries(
                PRICE_FIELDS.map((field) => [
                    field,
                    shown.includes(field) && data[field] !== ""
                        ? (data[field] ?? null)
                        : null,
                ]),
            ),
        };

        const onSuccess = (saved) => {
            onCloseWithUpdate(saved);
        };

        const onError = (response) => {
            setErrorMessages(response);
            const non_field_errors = Object.entries(response)
                .filter(([key]) => key === "non_field_errors")
                .map(([key, value]) => value);
            if (non_field_errors.length > 0) {
                setAlertMessage(non_field_errors.join("; "));
                setAlertOpen(true);
            }
        };

        if (data.id === undefined) {
            meteringTariffApi.create(payload, onSuccess, onError);
        } else {
            meteringTariffApi.update(
                `${meteringTariffApi.baseUrl}${data.id}/`,
                payload,
                onSuccess,
                onError,
            );
        }
    };

    return (
        <React.Fragment>
            <Dialog
                open={open}
                onClose={handleCloseWithoutSaving}
                fullScreen={fullScreen}
                fullWidth
                maxWidth={"md"}
            >
                <DialogTitle>
                    {data?.id === undefined ? t("Add Tariff") : t("Edit tariff")}
                </DialogTitle>
                <DialogContent>
                    <TariffEditForm
                        value={data}
                        onChange={handleFormChange}
                        errorMessages={errorMessages}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseWithoutSaving} autoFocus>
                        {t("Cancel")}
                    </Button>
                    <Button onClick={handleSave}>{t("Save")}</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={alertOpen} onClose={() => setAlertOpen(false)}>
                <DialogTitle id="alert-dialog-title">
                    {t("Problem saving data")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlertOpen(false)} autoFocus>
                        {t("OK")}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
